import {useMemo} from 'react'
import {tripFlightRecord} from '@/features/fromAirportArrival/tripFlightRecord'
import {formatWalkSummary, resolveWalkPlan} from '@/features/navigation/airportWalk'
import {yyzStaticWalkTotals} from '@/features/navigation/data/yyzStaticWalkSteps'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import WalkToPickup from '@/layouts/WalkToPickup'
import WalkToPickupSteps from '@/layouts/WalkToPickupSteps'
import {FindingDriver} from '@/customComponents/BookRideSteps/modalPages/findingDriver.jsx'
import {navIcon, rightArrow, arrowRightSm, personWithBags, carIcon} from '@img/index.js'

/**
 * The landed passenger's two cards — "Your next step" (luggage, driver, delay) and the
 * walk card (start walking, I've arrived, step-by-step directions) — plus the walk sheet
 * they open.
 *
 * Shared by the arrival guide (`FromAirportArrived`, inside `WelcomeModal`) and the
 * from-airport `/track-driver` page, so the two show the same cards. The caller owns what
 * is different between them: the chat ("Experiencing a delay?"), what "I've arrived" does
 * after it has told the driver, and what finishing the walk closes.
 *
 * `walkView` is controlled by the caller because the chat can open the walk too
 * ("start walking" from inside the thread).
 */

const TERMINAL_NAME = {T1: 'Terminal 1', T3: 'Terminal 3'}

/** Mint disc behind a step's glyph — the arrival flow's own accent, used nowhere as decoration. */
const STEP_DISC = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-(--accent-green-light) text-(--text-primary) max-md:h-9 max-md:w-9'

/*
 * ⚠️ THE STEP ICONS ARE THE SHIPPED ARTWORK, NOT HAND-DRAWN GLYPHS. `person-with-bags`
 * is the mobile app's own airport-guide icon, so the two products show a passenger the
 * same picture for the same instruction. They are dark artwork on a mint disc, which is
 * why they are <img> rather than `currentColor` SVGs like the rest of this page.
 */

const PinGlyph = () => (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 21s7-5.7 7-11a7 7 0 1 0-14 0c0 5.3 7 11 7 11z"/>
        <circle cx="12" cy="10" r="2.5"/>
    </svg>
)

const TickGlyph = () => (
    <svg className="h-5 w-5 max-md:h-4 max-md:w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9"/>
        <path d="m8.5 12.2 2.4 2.4 4.6-4.8"/>
    </svg>
)

const ListGlyph = () => (
    <svg className="h-5 w-5 max-md:h-4 max-md:w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01"/>
    </svg>
)

/**
 * A row in the action card: grey disc, label, chevron.
 *
 * ⚠️ THE CHEVRON IS THE SHARED ASSET, DIMMED — not a second chevron drawn grey. One
 * arrow shape on the page, at two weights, is what makes the dark button read as the
 * primary of the same family rather than as something unrelated.
 */
function ActionRow({glyph, label, onClick}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full cursor-pointer items-center gap-4 border-0 bg-transparent px-0 py-3 text-left hover:opacity-70 max-md:gap-3 max-md:py-2"
        >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-(--bg-grey) text-(--text-primary) max-md:h-8 max-md:w-8">
                {glyph}
            </span>
            <span className="flex-1 text-lg font-bold text-(--text-primary) max-md:text-sm">{label}</span>
            <img src={arrowRightSm} alt="" className="h-4 w-4 shrink-0 opacity-40"/>
        </button>
    )
}

/** One numbered thing to do, with its glyph. */
function NextStep({glyph, title, detail}) {
    return (
        <div className="flex items-center gap-4 max-md:gap-3">
            <span className={STEP_DISC}>{glyph}</span>
            <span className="min-w-0">
                <span className="block text-lg font-bold text-(--text-primary) max-md:text-sm">{title}</span>
                {detail && <span className="block text-(--text-secondary) max-md:text-xs">{detail}</span>}
            </span>
        </div>
    )
}

export default function ArrivalNextSteps({
    trip,
    tripId,
    walkView,
    onWalkViewChange,
    onOpenChat,
    onArrived,
    onWalkDone,
}) {
    const flight = tripFlightRecord(trip)

    /*
     * ⚠️ THE POSITION IS NOT PASSED IN, AND THAT IS THE FIX FOR A REAL BUG.
     *
     * This screen used to show the straight-line distance from the passenger to the
     * pickup, which is a sound idea exactly once — when they are already standing in the
     * terminal. Everywhere else it is the distance to Toronto, and it rendered as
     * "140644 min walk".
     *
     * The recorded walk is the better answer in every case anyway: it is the product
     * owner's own measured route from this terminal's arrivals door to the booth, so it is
     * true before the browser has given a position and still true if it never does.
     */
    const plan = useMemo(() => resolveWalkPlan(null, flight), [flight])
    const totals = useMemo(() => yyzStaticWalkTotals(plan.terminal, plan.scope), [plan.terminal, plan.scope])

    /* ⚠️ THE TIME IS PACED FOR LUGGAGE, NOT READ OFF THE RECORDING — the recorded 80
       seconds for Terminal 1's 136 m international walk is 6 km/h, and "about 1 min" reads
       as a screen that has not understood the question. See `formatWalkSummary`. */
    const walkSummary = formatWalkSummary(totals)
    /* ⚠️ ONLY NAMED WHEN SOMETHING NAMED IT. The walk plan always resolves to a terminal,
       because every table it indexes needs one — but when neither the flight's arrival
       stand nor a browser position said which, that terminal is a stand-in. Printing
       "Terminal 1" on a guess sends a passenger across the wrong building with full
       confidence; "Your terminal" is the same sentence without the claim. */
    const terminalName = plan.terminalSource === 'fallback'
        ? 'Your terminal'
        : TERMINAL_NAME[plan.terminal] || 'Your terminal'

    /* The belt the provider named for THIS arrival. Absent on plenty of flights — the
       field is optional and is not published until the aircraft is close — so the step
       below says "baggage claim" until there is a number to say instead. */
    const baggageBelt = String(flight?.arrival?.baggageBelt ?? '').trim() || null

    return (
        <>
            <div className="white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                <h4 className="mb-6! max-md:mb-3! max-md:text-sm!">Your next step</h4>

                <div className="flex flex-col gap-5 max-md:gap-3">
                    {/* ⚠️ THE BELT NUMBER IS THE PROVIDER'S, NOT A GUESS. Naming a
                        belt the flight did not name would send a passenger to
                        stand at someone else's carousel — so it is only said when
                        `arrival.baggageBelt` says it. */}
                    <NextStep
                        glyph={<img src={personWithBags} alt="" className="h-6 w-auto max-md:h-5"/>}
                        title="Collect Your Luggage"
                        detail={baggageBelt
                            ? `Clear immigration and customs, then baggage belt ${baggageBelt}.`
                            : 'Clear immigration and customs, then baggage claim.'}
                    />
                    <NextStep
                        glyph={<img src={carIcon} alt="" className="h-5 w-auto max-md:h-4"/>}
                        title="Meet Your Driver"
                        detail={walkSummary}
                    />
                </div>

                <div className="my-6 h-px bg-(--border-color) max-md:my-3"/>

                {/* ⚠️ IN THE CARD, NOT AMONG THE BUTTONS. A delay is what you report
                    when the plan above is not going to work, so it belongs with the
                    plan rather than beside the action that assumes it holds.

                    ⚠️ IT OPENS THE DRIVER CHAT — one thread with the driver, not a form of
                    its own. The caller owns the chat. */}
                <button
                    type="button"
                    onClick={onOpenChat}
                    className="w-full cursor-pointer rounded-(--radius-md) border-0 bg-transparent p-0 text-left hover:opacity-70"
                >
                    <span className="flex items-center gap-2 font-bold text-(--text-primary) max-md:text-sm">
                        Experiencing a delay?
                        <img src={rightArrow} alt="" className="h-3 w-auto"/>
                    </span>
                    <span className="block text-(--text-secondary) max-md:text-xs">
                        Immigration, customs or lost baggage — let your driver know.
                    </span>
                </button>
            </div>

            {/*
                ⚠️ ONE CARD, THREE WAYS THROUGH THE SAME MOMENT. The walk, the
                written directions, and saying you are already there — they answer
                the same question and belong in one place under the portal's own
                shadow (`white-global-section`), not scattered as loose buttons.
                The dark pill is the recommended path; the two rows under the rule
                are the alternatives, quieter by weight rather than by size.
            */}
            <div className="white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                <button
                    type="button"
                    onClick={() => onWalkViewChange('map')}
                    className="flex min-h-14 w-full cursor-pointer items-center gap-3 rounded-full border-0 bg-(--text-primary) px-4 font-semibold text-(--bg-white) max-md:min-h-11 max-md:gap-2 max-md:px-3 max-md:text-sm"
                >
                    <img src={navIcon} alt="" className="h-8 w-8 shrink-0 max-md:h-6 max-md:w-6"/>
                    {/* The label is the flexible element between two caps; the right
                        cap matches the left so it stays optically centred rather
                        than being pushed across by the icon. */}
                    <span className="flex-1 text-center">Start Walking to Pickup Area</span>
                    <span className="flex w-8 shrink-0 justify-center">
                        {/* The shared chevron, inverted to white — it is drawn dark
                            for light rows and this is the one dark button it sits on. */}
                        <img src={arrowRightSm} alt="" className="h-4 w-4 brightness-0 invert"/>
                    </span>
                </button>

                {/* Where the walk ends, directly under the button that starts it. */}
                <p className="mt-4 mb-0! flex items-center gap-3 text-(--text-secondary) max-md:mt-3 max-md:gap-2 max-md:text-xs">
                    <PinGlyph/>
                    {terminalName}, {plan.endLabel}, pre-arranged limo area
                </p>

                <div className="my-4 h-px bg-(--border-color) max-md:my-2"/>

                {/* "I've arrived" — the caller tells the driver (see `useAnnounceArrival`)
                    and decides where the passenger goes next. No `onArrived`, no row: the
                    /track-driver page has its own "Already at the pickup point?" card. */}
                {onArrived && (
                    <>
                        <ActionRow
                            glyph={<TickGlyph/>}
                            label={"I’ve arrived"}
                            onClick={onArrived}
                        />

                        {/* Inset to the text, not the card — it separates two rows of the
                            same list rather than two sections. */}
                        <div className="ml-16 h-px bg-(--border-color) max-md:ml-11"/>
                    </>
                )}

                {/* ⚠️ A FIRST-CLASS ALTERNATIVE, not a footnote. Indoor GPS is poor, so
                    the written steps are a route of their own — quieter than the walk
                    because the walk is recommended, never because they are a fallback. */}
                <ActionRow
                    glyph={<ListGlyph/>}
                    label="View Step-by-Step Directions"
                    onClick={() => onWalkViewChange('steps')}
                />
            </div>

            <Modal
                isOpen={!!walkView}
                onClose={() => onWalkViewChange(null)}
                /* ⚠️ BOTH WALKS ARE FULL-BLEED, and for the same reason: each is a stage
                   with its own bottom panel, so the sheet's reading gutters would frame it
                   in a white border and leave a band under it. Only the written GUIDE is
                   ordinary content that wants them.

                   ⚠️ `flushBody` IS SEPARATE FROM `flushX`. The side padding and the
                   bottom padding are two different rules in `Modal` — `flushX` alone left a
                   white strip under the map. */
                flushX={walkView !== 'guide'}
                flushTop={walkView === 'map'}
                flushBody={walkView !== 'guide'}
            >
                <ModalHeader
                    title="Walk to Pickup Point"
                    onClose={() => onWalkViewChange(null)}
                    /* ⚠️ BACK ONLY WHERE THERE IS SOMEWHERE TO GO BACK TO. The guide is the
                       only view reached from inside another, so it is the only one that
                       gets the arrow — a back control on the first view of a sheet is a
                       second close button wearing the wrong icon. */
                    onBack={walkView === 'guide' ? () => onWalkViewChange('steps') : undefined}
                />
                {walkView === 'map' && <WalkToPickup tripId={tripId} onLeave={onWalkDone}/>}
                {walkView === 'steps' && (
                    <WalkToPickupSteps tripId={tripId} onLeave={onWalkDone} onOpenGuide={() => onWalkViewChange('guide')}/>
                )}
                {walkView === 'guide' && <div className="hopn-walk-guide"><FindingDriver hideHeader/></div>}
            </Modal>
        </>
    )
}

export {ArrivalNextSteps}
